import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';
import ErrorBoundary from '@/components/ErrorBoundary';

// Route-level code splitting — admin pages are lazy-loaded
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CreateEvent = lazy(() => import('@/pages/CreateEvent'));
const EditEvent = lazy(() => import('@/pages/EditEvent'));
const ViewRegistrants = lazy(() => import('@/pages/ViewRegistrants'));
const EventLanding = lazy(() => import('@/pages/EventLanding'));
const AdminManagement = lazy(() => import('@/pages/AdminManagement'));

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite"><p className="text-gray-600">Loading...</p></div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/event/:slug" element={<EventLanding />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<Login />} />
            <Route
              path="/admin/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/events/create"
              element={
                <PrivateRoute>
                  <CreateEvent />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/events/:id/edit"
              element={
                <PrivateRoute>
                  <EditEvent />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/events/:id/registrants"
              element={
                <PrivateRoute>
                  <ViewRegistrants />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute>
                  <AdminManagement />
                </PrivateRoute>
              }
            />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/admin/dashboard" />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
